import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { MultiSelect } from "components/inputs/MultiSelect"
import { Toggle } from "components/inputs/Toggle"
import { GameFilterSelect, GameFilterValue } from "components/tournaments/GameFilterSelect"

import { settingsService } from "services/settings/settings.service"
import { VipTier } from "services/settings/type"

import {
  FAKE_BETS_MAX_VOLUME_PER_HOUR,
  FAKE_BETS_NAME_POOL_MODES,
  FAKE_BETS_PLAYER_MODES,
  FAKE_BETS_STRATEGIES,
  FakeBetPlayer,
  FakeBetsConfig
} from "types/api"
import { GameFilterType } from "types/tournament.types"

const DEFAULT_CONFIG: FakeBetsConfig = {
  enabled: false,
  volumePerHour: 0,
  strategy: "boost",
  playerMode: "namePool",
  namePool: { mode: "DEFAULT_POOL" },
  gameSelection: { mode: "random" },
  allowedCoins: [],
  betAmount: { min: 0, max: 0 },
  winRate: { probability: 0.4, multiplierMin: 1, multiplierMax: 3 },
  highRollerBoost: { probability: 0.05 }
}

const GAME_FILTER_TYPE_TO_MODE: Record<GameFilterType, FakeBetsConfig["gameSelection"]["mode"]> = {
  ALL: "random",
  SPECIFIC_GAMES: "specific",
  CATEGORY: "random", // not surfaced by our mode set — GameFilterSelect never emits this for our usage
  PROVIDER: "provider"
}
const MODE_TO_GAME_FILTER_TYPE: Record<FakeBetsConfig["gameSelection"]["mode"], GameFilterType> = {
  random: "ALL",
  specific: "SPECIFIC_GAMES",
  provider: "PROVIDER"
}

const inputClass =
  "text-sm rounded-md border border-black/20 dark:border-white/20 bg-white dark:bg-[#0C0C1A] text-gray-800 dark:text-white px-3 py-2 disabled:opacity-50"
const selectClass = `${inputClass} w-full sm:w-auto sm:shrink-0`

interface FakeBetsTabProps {
  currencies: Record<string, boolean>
  vipTiers: VipTier[]
}

export const FakeBetsTab: FC<FakeBetsTabProps> = ({ currencies, vipTiers }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [config, setConfig] = useState<FakeBetsConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [seedCount, setSeedCount] = useState(500)

  useEffect(() => {
    settingsService
      .getFakeBetsConfig()
      .then((cfg) => setConfig(cfg))
      .finally(() => setLoading(false))
  }, [])

  const value = config ?? DEFAULT_CONFIG
  const onChange = setConfig

  const currencyOptions = Object.keys(currencies).map((c) => ({ value: c, label: c }))
  const namePoolEntries = value.namePool.entries ?? []
  const hasInvalidPlayers =
    value.namePool.mode === "CUSTOM_POOL" && namePoolEntries.some((p) => !p.username.trim() || !p.display_name.trim())

  const strategyLabels: Record<FakeBetsConfig["strategy"], string> = {
    boost: t("settings.fakeBets.strategy.boost") as string,
    fill: t("settings.fakeBets.strategy.fill") as string
  }
  const playerModeLabels: Record<FakeBetsConfig["playerMode"], string> = {
    incognito: t("settings.fakeBets.playerMode.incognito") as string,
    namePool: t("settings.fakeBets.playerMode.namePool") as string,
    random: t("settings.fakeBets.playerMode.random") as string
  }
  const namePoolModeLabels: Record<FakeBetsConfig["namePool"]["mode"], string> = {
    DEFAULT_POOL: t("settings.fakeBets.namePool.defaultPool") as string,
    CUSTOM_POOL: t("settings.fakeBets.namePool.customPool") as string
  }

  const handleSave = async () => {
    if (hasInvalidPlayers) {
      toast.error(t("settings.fakeBets.namePool.validationError") as string)
      return
    }
    setSaving(true)
    try {
      const success = await settingsService.updateFakeBetsConfig(value)
      if (success) toast.success(t("settings.fakeBets.updated") as string)
    } finally {
      setSaving(false)
    }
  }

  const handleSeed = async () => {
    setSeeding(true)
    try {
      const success = await settingsService.seedFakeBets(seedCount)
      if (success) toast.success(t("settings.fakeBets.seedNow.started", { count: seedCount }) as string)
    } finally {
      setSeeding(false)
    }
  }

  const gameFilterValue: GameFilterValue = {
    game_filter_type: MODE_TO_GAME_FILTER_TYPE[value.gameSelection.mode],
    game_filter_ids: value.gameSelection.gameIds ?? [],
    provider_filter_ids: value.gameSelection.providerIds ?? []
  }

  const handleGameFilterChange = (next: GameFilterValue) => {
    onChange({
      ...value,
      gameSelection: {
        mode: GAME_FILTER_TYPE_TO_MODE[next.game_filter_type],
        gameIds: next.game_filter_ids,
        providerIds: next.provider_filter_ids
      }
    })
  }

  const addPlayer = () => {
    onChange({
      ...value,
      namePool: { ...value.namePool, entries: [...namePoolEntries, { username: "", display_name: "" }] }
    })
  }

  const removePlayer = (index: number) => {
    onChange({
      ...value,
      namePool: { ...value.namePool, entries: namePoolEntries.filter((_, i) => i !== index) }
    })
  }

  const updatePlayer = (index: number, patch: Partial<FakeBetPlayer>) => {
    onChange({
      ...value,
      namePool: { ...value.namePool, entries: namePoolEntries.map((p, i) => (i === index ? { ...p, ...patch } : p)) }
    })
  }

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t("settings.fakeBets.loading")}</p>
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h4 className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">{t("settings.fakeBets.sectionTitle")}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">{t("settings.fakeBets.sectionDescription")}</p>
      </div>

      <div className="flex flex-col max-w-2xl gap-4 rounded-lg border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-white/[0.04]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.enabled.label")}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.fakeBets.enabled.description")}</span>
          </div>
          <Toggle checked={value.enabled} onChange={() => onChange({ ...value, enabled: !value.enabled })} disabled={saving} />
        </div>

        {value.enabled && (
          <>
            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.volumePerHour.label")}</label>
              <input
                type="number"
                min={0}
                max={FAKE_BETS_MAX_VOLUME_PER_HOUR}
                value={value.volumePerHour}
                onChange={(e) =>
                  onChange({ ...value, volumePerHour: Math.min(FAKE_BETS_MAX_VOLUME_PER_HOUR, Number(e.target.value) || 0) })
                }
                disabled={saving}
                className={`${inputClass} w-40`}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t("settings.fakeBets.volumePerHour.cappedNotice", { max: FAKE_BETS_MAX_VOLUME_PER_HOUR })}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.strategy.label")}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.fakeBets.strategy.description")}</span>
              </div>
              <select
                value={value.strategy}
                onChange={(e) => onChange({ ...value, strategy: e.target.value as FakeBetsConfig["strategy"] })}
                disabled={saving}
                className={selectClass}
              >
                {FAKE_BETS_STRATEGIES.map((s) => (
                  <option key={s} value={s}>
                    {strategyLabels[s]}
                  </option>
                ))}
              </select>
            </div>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.playerMode.label")}</span>
              <select
                value={value.playerMode}
                onChange={(e) => onChange({ ...value, playerMode: e.target.value as FakeBetsConfig["playerMode"] })}
                disabled={saving}
                className={selectClass}
              >
                {FAKE_BETS_PLAYER_MODES.map((m) => (
                  <option key={m} value={m}>
                    {playerModeLabels[m]}
                  </option>
                ))}
              </select>
            </div>

            {value.playerMode !== "incognito" && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.namePool.label")}</span>
                  <select
                    value={value.namePool.mode}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        namePool: { ...value.namePool, mode: e.target.value as FakeBetsConfig["namePool"]["mode"] }
                      })
                    }
                    disabled={saving}
                    className={`${inputClass} py-1`}
                  >
                    {FAKE_BETS_NAME_POOL_MODES.map((m) => (
                      <option key={m} value={m}>
                        {namePoolModeLabels[m]}
                      </option>
                    ))}
                  </select>
                </div>

                {value.namePool.mode === "CUSTOM_POOL" && (
                  <div className="flex flex-col gap-2">
                    {namePoolEntries.map((p, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-2">
                        <input
                          type="text"
                          required
                          placeholder={t("settings.fakeBets.namePool.usernamePlaceholder") as string}
                          value={p.username}
                          onChange={(e) => updatePlayer(i, { username: e.target.value })}
                          disabled={saving}
                          className={clsx(
                            `${inputClass} py-1 flex-1 min-w-[8rem]`,
                            !p.username.trim() && "border-red-500 dark:border-red-500"
                          )}
                        />
                        <input
                          type="text"
                          required
                          placeholder={t("settings.fakeBets.namePool.displayNamePlaceholder") as string}
                          value={p.display_name}
                          onChange={(e) => updatePlayer(i, { display_name: e.target.value })}
                          disabled={saving}
                          className={clsx(
                            `${inputClass} py-1 flex-1 min-w-[8rem]`,
                            !p.display_name.trim() && "border-red-500 dark:border-red-500"
                          )}
                        />
                        <input
                          type="text"
                          placeholder={t("settings.fakeBets.namePool.avatarPlaceholder") as string}
                          value={p.avatar_url ?? ""}
                          onChange={(e) => updatePlayer(i, { avatar_url: e.target.value || undefined })}
                          disabled={saving}
                          className={`${inputClass} py-1 flex-1 min-w-[8rem]`}
                        />
                        <select
                          value={p.tierId ?? ""}
                          onChange={(e) => updatePlayer(i, { tierId: e.target.value || undefined })}
                          disabled={saving}
                          className={`${inputClass} py-1`}
                        >
                          <option value="">{t("settings.fakeBets.namePool.noTier")}</option>
                          {vipTiers.map((tier) => (
                            <option key={tier.id} value={tier.id}>
                              {tier.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removePlayer(i)}
                          disabled={saving}
                          className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50 px-2"
                        >
                          {t("settings.fakeBets.namePool.remove")}
                        </button>
                      </div>
                    ))}
                    {hasInvalidPlayers && <span className="text-xs text-red-500">{t("settings.fakeBets.namePool.validationError")}</span>}
                    <button
                      type="button"
                      onClick={addPlayer}
                      disabled={saving || hasInvalidPlayers}
                      className="text-xs self-start px-3 py-1.5 rounded-md border border-black/20 dark:border-white/20 text-gray-700 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                    >
                      {t("settings.fakeBets.namePool.addPlayer")}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.gameSelection.label")}</span>
              <GameFilterSelect value={gameFilterValue} onChange={handleGameFilterChange} />
            </div>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-2 relative mb-10">
              <MultiSelect
                className="h-10"
                title={t("settings.fakeBets.allowedCoins.label") as string}
                options={currencyOptions}
                value={value.allowedCoins}
                onChange={(allowedCoins) => onChange({ ...value, allowedCoins })}
                allLabel={t("settings.fakeBets.allowedCoins.selectCoins") as string}
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.fakeBets.allowedCoins.description")}</span>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.betAmount.label")}</span>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.fakeBets.betAmount.min")}</label>
                  <input
                    type="number"
                    min={0}
                    value={value.betAmount.min}
                    onChange={(e) => onChange({ ...value, betAmount: { ...value.betAmount, min: Number(e.target.value) || 0 } })}
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.fakeBets.betAmount.max")}</label>
                  <input
                    type="number"
                    min={0}
                    value={value.betAmount.max}
                    onChange={(e) => onChange({ ...value, betAmount: { ...value.betAmount, max: Number(e.target.value) || 0 } })}
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.fakeBets.betAmount.description")}</span>
            </div>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.winRate.label")}</span>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.fakeBets.winRate.probability")}</label>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={value.winRate.probability}
                    onChange={(e) => onChange({ ...value, winRate: { ...value.winRate, probability: Number(e.target.value) || 0 } })}
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.fakeBets.winRate.multiplierMin")}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={value.winRate.multiplierMin}
                    onChange={(e) => onChange({ ...value, winRate: { ...value.winRate, multiplierMin: Number(e.target.value) || 0 } })}
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs text-gray-600 dark:text-gray-300">{t("settings.fakeBets.winRate.multiplierMax")}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={value.winRate.multiplierMax}
                    onChange={(e) => onChange({ ...value, winRate: { ...value.winRate, multiplierMax: Number(e.target.value) || 0 } })}
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-black/10 dark:border-white/10" />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-800 dark:text-white">{t("settings.fakeBets.highRollerBoost.label")}</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.01}
                value={value.highRollerBoost.probability}
                onChange={(e) => onChange({ ...value, highRollerBoost: { probability: Number(e.target.value) || 0 } })}
                disabled={saving}
                className={`${inputClass} w-40`}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">{t("settings.fakeBets.highRollerBoost.description")}</span>
            </div>
          </>
        )}
      </div>

      <div className="flex gap-2">
        <SaveButton onClick={handleSave} loading={saving} disabled={hasInvalidPlayers}>
          {t("common.save")}
        </SaveButton>
      </div>

      <div className="flex flex-col max-w-2xl gap-3 rounded-lg border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-white/[0.04]">
        <div>
          <h4 className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">{t("settings.fakeBets.seedNow.title")}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("settings.fakeBets.seedNow.description")}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            type="number"
            min={1}
            max={2000}
            value={seedCount}
            onChange={(e) => setSeedCount(Math.min(2000, Math.max(1, Number(e.target.value) || 1)))}
            disabled={seeding}
            className={`${inputClass} w-full sm:w-32`}
          />
          <SaveButton onClick={handleSeed} loading={seeding} className="w-full sm:w-1/3">
            {t("settings.fakeBets.seedNow.seed")}
          </SaveButton>
        </div>
      </div>
    </div>
  )
}
